/**
 * Pure balance fetchers used by `useWallet`'s TanStack queries.
 *
 * Extracted so the query functions are testable without mounting the
 * hook. They live in the `/hooks/wallet/` folder alongside the other
 * wallet primitives rather than in `/lib/fetchers/` because both
 * entry points are bound to the hook's query keys; future wallet-
 * specific fetchers can drop in here too.
 */

import { type Hex } from 'viem';
import { TAKEOVER_TOKEN_ADDRESS } from '@/lib/constants/addresses';
import { ERC20_ABI } from '@/lib/contracts';
import { clientPublicClient } from '@/lib/viem-client';

/**
 * Fetches the viewer's USDC balance through our rate-limited RPC
 * proxy (`/api/rpc/balance/:addr`). `fresh=1` bypasses the short-
 * lived server-side cache — used after a balance-mutating action
 * (claim, fund) so the user sees their new balance immediately.
 */
export async function fetchUsdcBalance(addr: string, fresh = false): Promise<bigint> {
  const normalized = addr.toLowerCase();
  const url = `/api/rpc/balance/${encodeURIComponent(normalized)}${fresh ? '?fresh=1' : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Balance fetch failed: ${res.status}`);
  const { data } = await res.json();
  return BigInt(data.balance);
}

/**
 * Fetches the viewer's TAKEOVER balance via the shared `clientPublicClient`,
 * which batches concurrent `balanceOf` calls across hooks (useWallet,
 * price pollers, live balances) into a single multicall instead of
 * opening a fresh HTTP transport every 45s.
 *
 * Returns `0n` when the token address isn't configured or isn't a valid
 * EVM address so the hook tree stays renderable in dev / preview
 * environments without a deployed TAKEOVER contract.
 */
export async function fetchTakeoverBalance(addr: string): Promise<bigint> {
  if (!TAKEOVER_TOKEN_ADDRESS || !/^0x[a-fA-F0-9]{40}$/i.test(TAKEOVER_TOKEN_ADDRESS)) {
    return 0n;
  }
  const balance = await clientPublicClient.readContract({
    address: TAKEOVER_TOKEN_ADDRESS as Hex,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: [addr as Hex],
  });
  return balance as bigint;
}
